import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { cn } from '@/lib/utils'

const components = {
  p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
  h1: ({ children }) => <h1 className="text-base font-bold mt-4 mb-2 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-bold mt-4 mb-1.5 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mt-3 mb-1 first:mt-0">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-medium mt-3 mb-1 first:mt-0">{children}</h4>,
  h5: ({ children }) => <h5 className="text-xs font-semibold mt-2 mb-0.5 first:mt-0">{children}</h5>,
  h6: ({ children }) => <h6 className="text-xs font-medium mt-2 mb-0.5 first:mt-0">{children}</h6>,
  code: ({ children, className }) => {
    if (className) {
      // block code inside <pre> — pre handles the container styling
      return <code className={cn('font-mono text-xs', className)}>{children}</code>
    }
    // inline code
    return (
      <code className="px-1 py-0.5 rounded text-[0.85em] font-mono bg-muted text-foreground/80">
        {children}
      </code>
    )
  },
  pre: ({ children }) => (
    <pre className="my-3 p-2.5 rounded-md bg-muted overflow-x-auto text-xs font-mono leading-relaxed">
      {children}
    </pre>
  ),
  ul: ({ children }) => <ul className="mb-3 pl-4 list-disc space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="mb-3 pl-4 list-decimal space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm leading-[1.7]">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-3 pl-3 border-l-2 border-border text-muted-foreground italic">
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline hover:opacity-80"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-border" />,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto">
      <table className="w-full text-xs border-collapse">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="px-2 py-1 bg-muted font-medium text-left border border-border">{children}</th>
  ),
  td: ({ children }) => <td className="px-2 py-1 border border-border">{children}</td>,
}

export default function MarkdownPreview({ content, className }) {
  return (
    <div className={cn('text-sm leading-[1.7] text-foreground/90', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  )
}
